<!-- =========================================================================================
	File Name: VxSidebarGroup.vue
	Description: Sidebar Group Component. Extends vuesax framework's 'vs-sidebar-group' component
	Component Name: VxSidebarGroup
	----------------------------------------------------------------------------------------
	Item Name: Vuesax Admin - VueJS Dashboard Admin Template
	Author: Pixinvent
	Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->


<template>
  <div
    :class="[
      { 'vs-sidebar-group-open': openItems },
      { 'vs-sidebar-group-active': open },
      { 'disabled-item pointer-events-none': group.isDisabled },
    ]"
    class="vs-sidebar-group"
    @mouseover="mouseover"
    @mouseout="mouseout"
  >
    <!-- When sidebar is collapsed, show dropdown on hover -->
    <vs-dropdown 
      v-if="sidebarItemsMin && group.submenu && group.submenu.length > 0"
      vs-custom-content 
      vs-trigger-hover
      :vs-delay="300"
      class="w-full"
      position="right"
      @mouseenter="onDropdownHover"
      @mouseleave="onDropdownLeave"
    >
      <div class="group-header w-full cursor-pointer">
        <span class="flex items-center w-full">
          <feather-icon
            :icon="group.icon || 'CircleIcon'"
            :svgClasses="{ 'w-3 h-3': this.groupIndex % 1 != 0 }"
            v-if="group.icon || this.groupIndex > Math.floor(this.groupIndex)"
          />
          <span class="vs-sidebar--tooltip">{{
            $t(group.i18n) || group.name
          }}</span>
        </span>
      </div>
      
      <vs-dropdown-menu class="sidebar-dropdown-menu" style="z-index: 51001; min-width: 200px;">
        <vs-dropdown-item 
          v-for="(subItem, index) in group.submenu" 
          :key="index"
          :to="subItem.slug != 'external' ? subItem.url : ''"
          :href="subItem.slug == 'external' ? subItem.url : ''"
          :target="subItem.target"
          class="sidebar-dropdown-item"
        >
          <feather-icon :icon="subItem.icon || 'CircleIcon'" svgClasses="w-4 h-4 mr-2" />
          <span>{{ $t(subItem.i18n) || subItem.name }}</span>
        </vs-dropdown-item>
      </vs-dropdown-menu>
    </vs-dropdown>
    
    <!-- When sidebar is expanded, show normal group behavior -->
    <div v-else @click="clickGroup" class="group-header w-full">
      <span class="flex items-center w-full">
        <feather-icon
          :icon="group.icon || 'CircleIcon'"
          :svgClasses="{ 'w-3 h-3': this.groupIndex % 1 != 0 }"
          v-if="group.icon || this.groupIndex > Math.floor(this.groupIndex)"
        />
        <span v-show="!sidebarItemsMin" class="truncate mr-3 select-none">{{
          $t(group.i18n) || group.name
        }}</span>
        <vs-chip
          class="ml-auto mr-4"
          :color="group.tagColor"
          v-if="group.tag && !sidebarItemsMin"
          >{{ group.tag }}</vs-chip
        >
      </span>
      <feather-icon
        icon="ChevronRightIcon"
        svg-classes="w-4 h-4"
        :class="[{ rotate90: openItems }, 'feather-grp-header-arrow']"
        v-show="!sidebarItemsMin"
      />
      <span class="vs-sidebar--tooltip">{{
        $t(group.i18n) || group.name
      }}</span>
    </div>
    <ul ref="items" :style="styleItems" class="vs-sidebar-group-items">
      <li v-for="(groupItem, index) in group.submenu" :key="index">
        <vx-sidebar-group
          :group="groupItem"
          :groupIndex="Number(`${groupIndex}.${index}`)"
          :open="isGroupActive(groupItem)"
          :openHover="openHover"
          v-if="groupItem.submenu"
        />
        <vx-sidebar-item
          :IsVisualStudio="
            groupItem.hideFormFactors &&
            groupItem.hideFormFactors.IsVisualStudio
          "
          :index="groupIndex + '.' + index"
          :to="groupItem.url"
          :icon="itemIcon(groupIndex + '.' + index)"
          icon-small
          :target="groupItem.target"
          :ShowForGuestUser="groupItem.ShowForGuestUser"
          v-else
        >
          <span class="truncate">{{
            $t(groupItem.i18n) || groupItem.name
          }}</span>
          <vs-chip
            class="ml-auto"
            :color="groupItem.tagColor"
            v-if="groupItem.tag"
            >{{ groupItem.tag }}</vs-chip
          >
        </vx-sidebar-item>
      </li>
    </ul>
  </div>
</template>

<script>
import VxSidebarItem from "./VxSidebarItem.vue";

export default {
  name: "vx-sidebar-group",
  props: {
    openHover: {
      default: false,
      type: Boolean,
    },
    open: {
      default: false,
      type: Boolean,
    },
    group: {
      type: Object,
    },
    groupIndex: {
      type: Number,
    },
    ShowForGuestUser: {
      default: false,
      type: Boolean,
    },
  },
  data: () => ({
    maxHeight: "0px",
    openItems: false,
    dropdownHoverTimeout: null,
  }),
  computed: {
    sidebarItemsMin() {
      return this.$store.state.sidebarItemsMin;
    },
    styleItems() {
      return {
        maxHeight: this.maxHeight,
      };
    },
    itemIcon() {
      return (index) => {
        if (!((index.match(/\./g) || []).length > 1)) return "CircleIcon";
      };
    },
    isGroupActive() {
      return (sidebarItem) => {
        const path = this.$route.fullPath;
        let open = false;
        let func = function (sidebarItem) {
          if (sidebarItem.submenu) {
            sidebarItem.submenu.forEach((item) => {
              if (path == item.url) {
                open = true;
              } else if (item.submenu) {
                func(item);
              }
            });
          }
        };

        func(sidebarItem);
        return open;
      };
    },
  },
  watch: {
    // OPEN & CLOSES DROPDOWN ON ROUTE CHANGE
    $route() {
      if (this.sidebarItemsMin) return;
      let scrollHeight = this.scrollHeight;
      if (this.openItems && !this.open) {
        this.maxHeight = `${0}px`;
      } else if (this.open) {
        this.maxHeight = "none";
      }
    },
    maxHeight() {
      this.openItems = this.maxHeight != "0px";
    },
    // OPEN AND CLOSES DROPDOWN MENU ON SIDEBAR COLLAPSE AND DEFAULT VIEW
    "$store.state.sidebarItemsMin"(val) {
      let scrollHeight = this.$refs.items.scrollHeight;
      if (!val && this.open) {
        this.maxHeight = "none";
      } else {
        this.maxHeight = "0px";
      }
      if (val && this.open) {
        this.maxHeight = "0px";
      }
    },
  },
  methods: {
    clickGroup() {
      if (!this.openHover) {
        let thisScrollHeight = this.$refs.items.scrollHeight;
        if (this.maxHeight == "0px") {
          this.maxHeight = "none";
        } else {
          this.maxHeight = "0px";
        }

        this.$parent.$children.map((child) => {
          if (child.isGroupActive) {
            if (child !== this && !child.open && child.maxHeight != "0px") {
              child.maxHeight = "0px";
            }
          }
        });
      }
    },
    mouseover() {
      if (this.openHover) {
        let scrollHeight = this.$refs.items.scrollHeight;
        this.maxHeight = `${scrollHeight}px`;
      }
    },
    mouseout() {
      if (this.openHover) {
        let scrollHeight = 0;
        this.maxHeight = `${scrollHeight}px`;
      }
    },
    onDropdownHover() {
      // Keep dropdown open when hovering over it
      this.dropdownHoverTimeout = null;
    },
    onDropdownLeave() {
      // Add delay before closing dropdown
      this.dropdownHoverTimeout = setTimeout(() => {
        // Close dropdown after delay
      }, 1000);
    },
  },
  components: {
    VxSidebarItem,
  },
  mounted() {
    this.openItems = this.open;
    if (this.open) {
      this.maxHeight = "none";
    }
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vuesax/components/vxSidebarGroup.scss";
</style>
